﻿import { AlertCircle, CalendarDays, Loader2, MapPin, Plus, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import type { EventStatus } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { QueryError } from "@/components/query-error";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useEventsQuery } from "@/features/events/api";
import { formatEventDate } from "@/features/events/date-helpers";
import { useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

const STATUS_VARIANTS: Record<EventStatus, "success" | "warning" | "secondary" | "destructive" | "outline" | "default"> = {
  Draft: "outline",
  Published: "success",
  Paused: "warning",
  Cancelled: "destructive",
  Ended: "secondary",
  Archived: "secondary",
};

type FilterValue = EventStatus | "active";

const FILTERS: readonly FilterValue[] = [
  "active",
  "Draft",
  "Published",
  "Paused",
  "Ended",
  "Cancelled",
  "Archived",
];

// "Aktif" görünümü: iptal ve arşiv dışındaki tüm statuslar (günlük çalışma akışı).
const ACTIVE_STATUSES: readonly EventStatus[] = [
  "Draft",
  "Published",
  "Paused",
  "Ended",
];

export function EventsListPage() {
  const t = useT("events");
  const tCommon = useT("common");
  const tStatus = useT("eventStatus");
  const fmt = useFormat();
  const [filter, setFilter] = useState<FilterValue>("active");
  // Backend tekil status filtresi destekliyor; "active" için tüm listeyi çekip client-side filtreliyoruz.
  const backendStatus = filter === "active" ? undefined : filter;
  const { data: rawData, isLoading, error, refetch, isRefetching } = useEventsQuery(backendStatus);
  const data =
    filter === "active" && rawData
      ? rawData.filter((e) => ACTIVE_STATUSES.includes(e.status))
      : rawData;

  return (
    <DashboardPage>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("title")}</h1>
          <p className="text-sm text-muted-foreground">{t("description")}</p>
        </div>
        <Button asChild>
          <Link to="/events/new">
            <Plus className="me-1.5 h-4 w-4" /> {t("newEvent")}
          </Link>
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f}
            variant={filter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f)}
          >
            {f === "active" ? t("filterActive") : tStatus(f)}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <QueryError
          message={t("loadError")}
          onRetry={() => refetch()}
          retrying={isRefetching}
        />
      ) : !data || data.length === 0 ? (
        <EmptyState t={t} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((evt) => (
            <Card key={evt.id} className="transition-colors hover:border-primary/40">
              <CardHeader className="space-y-2">
                <div className="flex items-center justify-between">
                  <CalendarDays className="h-5 w-5 text-muted-foreground" />
                  <Badge variant={STATUS_VARIANTS[evt.status]}>
                    {tStatus(evt.status)}
                  </Badge>
                </div>
                <CardTitle className="line-clamp-2 text-lg">{evt.title}</CardTitle>
                <CardDescription className="flex items-center gap-1 text-xs">
                  <MapPin className="h-3 w-3" />
                  {evt.venueCity ?? t("noVenue")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm">
                  <div className="text-muted-foreground">{t("startsAt")}</div>
                  <div className="font-medium">{formatEventDate(evt.startsAt, fmt)}</div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Users className="h-3 w-3" />
                  {t("sold", {
                    sold: fmt.number(evt.totalSold),
                    capacity: evt.totalCapacity ? fmt.number(evt.totalCapacity) : "—",
                  })}
                  <span>·</span>
                  {t("ticketTypes", { count: evt.ticketTypeCount })}
                </div>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <Link to={`/events/${evt.slug}`}>{tCommon("manage")}</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </DashboardPage>
  );
}

function EmptyState({ t }: { t: ReturnType<typeof useT> }) {
  return (
    <Card>
      <CardHeader className="items-center text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <AlertCircle className="h-6 w-6 text-muted-foreground" />
        </div>
        <CardTitle>{t("emptyTitle")}</CardTitle>
        <CardDescription>{t("emptyDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="flex justify-center">
        <Button asChild>
          <Link to="/events/new">
            <Plus className="me-1.5 h-4 w-4" /> {t("emptyCta")}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
